import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI, companiesAPI, authAPIExt, OrgInvitePreview } from '../services/api';
import { Company } from '../types';

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    role: 'candidate' as 'recruiter' | 'candidate' | 'partner',
    phone: '',
    company_id: '',
    company_name: '',
    organization_website: '',
    invite_token: '',
    coupon_code: '',
    department: '',
    current_location: '',
    preferred_location: '',
    experience_years: '',
    experience_months: '',
    profile_summary: ''
  });
  const [affiliateCode, setAffiliateCode] = useState('');
  const [affiliateOffer, setAffiliateOffer] = useState<any | null>(null);
  const [partnerProfileToken, setPartnerProfileToken] = useState('');
  const [partnerInviteMeta, setPartnerInviteMeta] = useState<{ partner_id?: number; expires_at?: string } | null>(null);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [searchParams] = useSearchParams();
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [success, setSuccess] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalUrl, setModalUrl] = useState('/terms.html');
  const [modalTitle, setModalTitle] = useState('Terms of Use');

  const [orgCapBlock, setOrgCapBlock] = useState<{ domain?: string; limit?: number; current?: number; message?: string } | null>(null);

  const [orgInviteToken, setOrgInviteToken] = useState<string>('');
  const [orgInvite, setOrgInvite] = useState<OrgInvitePreview | null>(null);
  const [orgInviteError, setOrgInviteError] = useState<string | null>(null);

  const { register } = useAuth();
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await companiesAPI.getCompanies();
        setCompanies(response.companies);
      } catch (error) {
        console.error('Failed to fetch companies:', error);
      }
    };

    fetchCompanies();
  }, []);

  useEffect(() => {
    const invite = searchParams.get('invite') || '';
    const coupon = searchParams.get('coupon') || '';
    const ref = (searchParams.get('ref') || '').trim();
    const partnerToken = (searchParams.get('partner_profile_token') || '').trim();
    setAffiliateCode(ref);
    if (ref) {
      const loadAffiliateOffer = async () => {
        try {
          const response = await authAPI.getAffiliateOffer(ref);
          setAffiliateOffer(response.offer || null);
        } catch (err: any) {
          setAffiliateOffer(null);
          setError(err?.response?.data?.error || err?.message || 'Affiliate offer is unavailable.');
        }
      };
      loadAffiliateOffer();
    } else {
      setAffiliateOffer(null);
    }
    setPartnerProfileToken(partnerToken);

    const orgInviteParam = (searchParams.get('org_invite_token') || '').trim();
    if (orgInviteParam) {
      setOrgInviteToken(orgInviteParam);
      (async () => {
        try {
          const preview = await authAPIExt.getOrgInvitePreview(orgInviteParam);
          if (preview.valid) {
            setOrgInvite(preview);
            setOrgInviteError(null);
            setFormData((prev) => ({
              ...prev,
              role: 'recruiter',
              email: preview.email || prev.email,
            }));
          } else {
            setOrgInvite(null);
            const reasonLabel =
              preview.reason === 'expired' ? 'expired' :
              preview.reason === 'used' ? 'already used' :
              preview.reason === 'revoked' ? 'revoked' :
              'not found';
            setOrgInviteError(`This invite link is ${reasonLabel}. Ask your Org Admin for a new one.`);
          }
        } catch (err: any) {
          setOrgInvite(null);
          setOrgInviteError(err?.response?.data?.error || err?.message || 'Failed to validate invite.');
        }
      })();
    }

    if (invite && coupon) {
      setFormData((prev) => ({
        ...prev,
        role: 'partner',
        invite_token: invite,
        coupon_code: coupon
      }));
    }
    if (partnerToken) {
      const loadPartnerInvite = async () => {
        try {
          const response = await authAPI.getPartnerProfileInvite(partnerToken);
          const inviteData = response.invite || {};
          setPartnerInviteMeta({
            partner_id: inviteData.partner_id,
            expires_at: inviteData.expires_at
          });
          setFormData((prev) => ({
            ...prev,
            role: 'candidate',
            email: inviteData.email || prev.email,
            first_name: inviteData.first_name || prev.first_name,
            last_name: inviteData.last_name || prev.last_name,
            phone: inviteData.phone || prev.phone,
            current_location: inviteData.current_location || prev.current_location,
            preferred_location: inviteData.preferred_location || prev.preferred_location,
            experience_years: inviteData.experience_years !== undefined && inviteData.experience_years !== null ? String(inviteData.experience_years) : prev.experience_years,
            experience_months: inviteData.experience_months !== undefined && inviteData.experience_months !== null ? String(inviteData.experience_months) : prev.experience_months,
            profile_summary: inviteData.profile_summary || prev.profile_summary
          }));
        } catch (err: any) {
          setError(err?.response?.data?.error || err?.message || 'Partner profile invite is invalid or expired.');
        }
      };
      loadPartnerInvite();
    }
  }, [searchParams]);

  const validatePasswordRules = (password: string) => {
    return {
      length: password.length >= 8,
      upper: /[A-Z]/.test(password),
      lower: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else {
      const rules = validatePasswordRules(formData.password);
      const missing = Object.entries(rules).filter(([_, ok]) => !ok);
      if (missing.length) {
        newErrors.password = 'Password must be 8+ chars and include upper, lower, number, and special character.';
      }
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!formData.first_name) newErrors.first_name = 'First name is required';
    if (!formData.last_name) newErrors.last_name = 'Last name is required';
    if (!acceptedTerms) newErrors.terms = 'You must accept the Terms of Use';

    if (formData.role === 'recruiter') {
      if (!formData.phone) newErrors.phone = 'Phone is required for recruiters';
      if (!formData.company_name) newErrors.company_name = 'Company name is required';
      if (!formData.organization_website) newErrors.organization_website = 'Company website is required';
    }

    if (formData.role === 'candidate') {
      if (formData.experience_years && isNaN(Number(formData.experience_years))) {
        newErrors.experience_years = 'Experience years must be a number';
      }
      if (formData.experience_months && (isNaN(Number(formData.experience_months)) || Number(formData.experience_months) < 0 || Number(formData.experience_months) > 11)) {
        newErrors.experience_months = 'Experience months must be between 0 and 11';
      }
    }

    if (partnerProfileToken && formData.role !== 'candidate') {
      newErrors.email = 'Partner profile invite can only be used for candidate registration.';
    }

    if (formData.role === 'partner') {
      if (!formData.invite_token || !formData.coupon_code) {
        newErrors.email = 'Invite link is required for partners.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Live check for whether all required fields are present + valid. Used to gate
  // the Create Account button so users can't submit with missing/invalid input.
  const isFormValid = (() => {
    if (!formData.email.trim()) return false;
    if (!formData.password) return false;
    const rules = validatePasswordRules(formData.password);
    if (!rules.length || !rules.upper || !rules.lower || !rules.number || !rules.special) return false;
    if (formData.password !== formData.confirmPassword) return false;
    if (!formData.first_name.trim()) return false;
    if (!formData.last_name.trim()) return false;
    if (!acceptedTerms) return false;
    if (formData.role === 'recruiter') {
      if (!formData.phone.trim()) return false;
      if (!formData.company_name.trim()) return false;
      if (!formData.organization_website.trim()) return false;
    }
    if (formData.role === 'candidate') {
      if (formData.experience_years && isNaN(Number(formData.experience_years))) return false;
      if (formData.experience_months) {
        const m = Number(formData.experience_months);
        if (isNaN(m) || m < 0 || m > 11) return false;
      }
    }
    if (formData.role === 'partner') {
      if (!formData.invite_token || !formData.coupon_code) return false;
    }
    if (partnerProfileToken && formData.role !== 'candidate') return false;
    return true;
  })();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, resume: 'Please upload a PDF, DOC, or DOCX file' }));
        return;
      }
      
      // Validate file size (16MB max)
      if (file.size > 16 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, resume: 'File size must be less than 16MB' }));
        return;
      }
      
      setResumeFile(file);
      // Clear error when valid file is selected
      if (errors.resume) {
        setErrors(prev => ({ ...prev, resume: '' }));
      }
    }
  };

  const openDocumentModal = (event: React.MouseEvent<HTMLAnchorElement>, url: string, title: string) => {
    event.preventDefault();
    setModalUrl(url);
    setModalTitle(title);
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError('');
    setSuccess('');
    setOrgCapBlock(null);

    try {
      const registrationData: any = {
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        role: formData.role,
        phone: formData.phone || undefined,
        accepted_terms: true
      };

      if (formData.role === 'recruiter') {
        const matchedCompany = companies.find(
          (c) => c.name.toLowerCase() === formData.company_name.trim().toLowerCase()
        );
        if (matchedCompany) {
          registrationData.company_id = matchedCompany.id;
        } else {
          registrationData.company_name = formData.company_name.trim();
          registrationData.organization_website = formData.organization_website;
        }
        registrationData.department = formData.department || undefined;
        registrationData.organization_website = registrationData.organization_website || formData.organization_website || undefined;
      }

      if (formData.role === 'candidate') {
        registrationData.current_location = formData.current_location || undefined;
        registrationData.preferred_location = formData.preferred_location || undefined;
        registrationData.experience_years = formData.experience_years ? parseInt(formData.experience_years) : undefined;
        registrationData.experience_months = formData.experience_months !== '' ? parseInt(formData.experience_months) || 0 : undefined;
        registrationData.profile_summary = formData.profile_summary || undefined;
        if (affiliateCode) {
          registrationData.affiliate_code = affiliateCode;
        }
        if (partnerProfileToken) {
          registrationData.partner_profile_token = partnerProfileToken;
        }
      }

      if (formData.role === 'partner') {
        registrationData.invite_token = formData.invite_token;
        registrationData.coupon_code = formData.coupon_code;
      }

      if (orgInviteToken && orgInvite) {
        registrationData.org_invite_token = orgInviteToken;
      }

      const response = await register(registrationData, resumeFile || undefined);
      let successMsg = response.message || 'Registration successful. Please check your email to verify your account.';
      // If a resume was attached but the server couldn't save it, tell the user
      // and point them to the profile page for a manual re-upload. The account
      // itself is created either way.
      if (resumeFile && (response as any).resume_saved === false) {
        const reason = (response as any).resume_error as string | undefined;
        const reasonText: Record<string, string> = {
          unsupported_extension: 'the file type wasn’t recognised',
          empty_file: 'the file was empty',
          file_too_large: 'the file exceeded 16 MB',
          upload_failed: 'the upload failed',
        };
        const hint = reason && reasonText[reason]
          ? ` (${reasonText[reason]})`
          : '';
        successMsg += ` We couldn’t save your resume${hint} — you can re-upload it from your profile after verifying your email.`;
      }
      setSuccess(successMsg);
    } catch (err: any) {
      if (err?.code === 'ORG_CAP_REACHED') {
        setOrgCapBlock({
          domain: err.data?.domain,
          limit: err.data?.limit,
          current: err.data?.current,
          message: err.data?.message || err.message,
        });
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const PasswordRequirements: React.FC<{ password: string }> = ({ password }) => {
    const rules = validatePasswordRules(password);
    const items = [
      { key: 'length', label: 'At least 8 characters', ok: rules.length },
      { key: 'upper', label: 'Uppercase letter (A-Z)', ok: rules.upper },
      { key: 'lower', label: 'Lowercase letter (a-z)', ok: rules.lower },
      { key: 'number', label: 'Number (0-9)', ok: rules.number },
      { key: 'special', label: 'Special character (!@#$...)', ok: rules.special },
    ];
    return (
      <ul className="password-requirements">
        {items.map((item) => (
          <li key={item.key} className={item.ok ? 'ok' : ''}>
            {item.ok ? '✓' : '•'} {item.label}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="container">
      <div className="center">
        <div className="card shadow" style={{ maxWidth: '500px', width: '100%' }}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <h1 className="h1">Join talixjobs</h1>
            <p className="muted">Create your account and find your next opportunity</p>
          </div>

          {orgInviteError && (
            <div className="alert error" style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Invite link unavailable</div>
              <div>{orgInviteError}</div>
              <button
                type="button"
                className="ui-btn ui-btn--ghost ui-btn--sm"
                style={{ marginTop: 8 }}
                onClick={() => {
                  setOrgInviteToken('');
                  setOrgInvite(null);
                  setOrgInviteError(null);
                  // Drop the param from the URL so a refresh doesn't re-trigger the lookup.
                  if (typeof window !== 'undefined') {
                    const url = new URL(window.location.href);
                    url.searchParams.delete('org_invite_token');
                    window.history.replaceState({}, '', url.toString());
                  }
                }}
              >
                <span className="ui-btn__label">Sign up without an invite</span>
              </button>
            </div>
          )}
          {orgInvite && (
            <div className="alert success" style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                You've been invited by {orgInvite.inviter_name}
              </div>
              <div>
                Join talixjobs as a recruiter for <strong>{orgInvite.org_domain}</strong>. Your email is
                pre-validated through this invitation, so you won't need a separate verification step.
              </div>
            </div>
          )}
          {orgCapBlock && (
            <div className="alert warning" style={{ marginBottom: '16px' }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                Your organization has reached its free recruiter limit
              </div>
              <div style={{ marginBottom: 10 }}>
                {orgCapBlock.message ||
                  'Contact our sales team to enable enterprise access for your team.'}
                {orgCapBlock.domain && orgCapBlock.limit !== undefined && (
                  <div style={{ marginTop: 6, fontSize: '0.92em' }}>
                    Domain: <strong>{orgCapBlock.domain}</strong>
                    {' · '}Free limit: <strong>{orgCapBlock.limit}</strong>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <Link to="/talk-to-sales" className="ui-btn ui-btn--primary ui-btn--sm">
                  <span className="ui-btn__label">Talk to sales</span>
                </Link>
                <button
                  type="button"
                  className="ui-btn ui-btn--secondary ui-btn--sm"
                  onClick={() => {
                    setOrgCapBlock(null);
                    setFormData((prev) => ({ ...prev, email: '' }));
                  }}
                >
                  <span className="ui-btn__label">Use a different email</span>
                </button>
              </div>
            </div>
          )}
          {error && (
            <div className="alert error">
              {error}
            </div>
          )}
          {success && (
            <div className="alert success">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {partnerProfileToken && (
              <div className="alert success" style={{ marginBottom: '16px' }}>
                Your profile was shared by a Partner{partnerInviteMeta?.partner_id ? ` (Partner ID: ${partnerInviteMeta.partner_id})` : ''}. Complete registration to verify your email and activate your account.
              </div>
            )}
            {formData.role === 'candidate' && affiliateCode && (
              <div className="alert success" style={{ marginBottom: '16px' }}>
                <div>You are joining through a partner affiliate referral link.</div>
                {affiliateOffer && (
                  <div style={{ marginTop: '6px' }}>
                    Component: <strong>{affiliateOffer.component_name || 'Affiliate Candidate Silver'}</strong> | Price: <strong>{affiliateOffer.currency || 'INR'} {affiliateOffer.amount_inr}</strong>
                  </div>
                )}
                {!affiliateOffer && (
                  <div style={{ marginTop: '6px' }}>
                    Affiliate pricing will be loaded before registration.
                  </div>
                )}
              </div>
            )}
            {/* Role Selection */}
            {formData.role !== 'partner' && !partnerProfileToken ? (
              <div className="form-group">
                <label className="label">I am a</label>
                <select
                  name="role"
                  className="select"
                  value={formData.role}
                  onChange={handleChange}
                  required
                >
                  <option value="candidate">Job Seeker</option>
                  <option value="recruiter">Recruiter</option>
                </select>
              </div>
            ) : formData.role !== 'partner' && partnerProfileToken ? (
              <div className="alert success" style={{ marginBottom: '16px' }}>
                You are registering as a Job Seeker from a partner profile invite.
              </div>
            ) : (
              <div className="alert success" style={{ marginBottom: '16px' }}>
                You are registering as a Profile Partner via an invite link.
              </div>
            )}

            {/* Basic Information */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="label">First Name</label>
                <input
                  type="text"
                  name="first_name"
                  className={`input ${errors.first_name ? 'error' : ''}`}
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  placeholder="John"
                />
                {errors.first_name && <div className="error-message">{errors.first_name}</div>}
              </div>

              <div className="form-group">
                <label className="label">Last Name</label>
                <input
                  type="text"
                  name="last_name"
                  className={`input ${errors.last_name ? 'error' : ''}`}
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  placeholder="Doe"
                />
                {errors.last_name && <div className="error-message">{errors.last_name}</div>}
              </div>
            </div>

            <div className="form-group">
              <label className="label">Email</label>
              <input
                type="email"
                name="email"
                className={`input ${errors.email ? 'error' : ''}`}
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="john@example.com"
                readOnly={Boolean(partnerProfileToken) || Boolean(orgInvite)}
              />
              {errors.email && <div className="error-message">{errors.email}</div>}
            </div>

            <div className="form-group">
              <label className="label">Phone {formData.role === 'recruiter' ? '(Required)' : '(Optional)'}</label>
              <input
                type="tel"
                name="phone"
                className={`input ${errors.phone ? 'error' : ''}`}
                value={formData.phone}
                onChange={handleChange}
                placeholder="+91-9876543210"
                required={formData.role === 'recruiter'}
              />
              {errors.phone && <div className="error-message">{errors.phone}</div>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="label">Password</label>
                <div className="password-field">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    className={`input ${errors.password ? 'error' : ''}`}
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="8+ chars, upper/lower/number/special"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <span className="eye-icon" aria-hidden="true">
                      {showPassword ? '\u{E801}' : '\u{E800}'}
                    </span>
                  </button>
                </div>
                {errors.password && <div className="error-message">{errors.password}</div>}
                <PasswordRequirements password={formData.password} />
              </div>

              <div className="form-group">
                <label className="label">Confirm Password</label>
                <div className="password-field">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    className={`input ${errors.confirmPassword ? 'error' : ''}`}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    placeholder="Repeat password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    <span className="eye-icon" aria-hidden="true">
                      {showConfirmPassword ? '\u{E801}' : '\u{E800}'}
                    </span>
                  </button>
                </div>
                {errors.confirmPassword && <div className="error-message">{errors.confirmPassword}</div>}
              </div>
            </div>

            {/* Recruiter-specific fields */}
            {formData.role === 'recruiter' && (
              <>
                <div className="form-group">
                  <label className="label">Company Name</label>
                  <input
                    type="text"
                    name="company_name"
                    list="company-suggestions"
                    className={`input ${errors.company_name ? 'error' : ''}`}
                    value={formData.company_name}
                    onChange={handleChange}
                    placeholder="Start typing to search companies"
                    required
                  />
                  <datalist id="company-suggestions">
                    {companies.map((company) => (
                      <option key={company.id} value={company.name} />
                    ))}
                  </datalist>
                  {errors.company_name && <div className="error-message">{errors.company_name}</div>}
                </div>

                <div className="form-group">
                  <label className="label">Company Website</label>
                  <input
                    type="url"
                    name="organization_website"
                    className={`input ${errors.organization_website ? 'error' : ''}`}
                    value={formData.organization_website}
                    onChange={handleChange}
                    placeholder="https://company.com"
                    required
                  />
                  {errors.organization_website && <div className="error-message">{errors.organization_website}</div>}
                </div>

                <div className="form-group">
                  <label className="label">Department (Optional)</label>
                  <input
                    type="text"
                    name="department"
                    className="input"
                    value={formData.department}
                    onChange={handleChange}
                    placeholder="e.g. Engineering, HR, Marketing"
                  />
                </div>
              </>
            )}

            {/* Candidate-specific fields */}
            {formData.role === 'candidate' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label className="label">Current Location (Optional)</label>
                    <input
                      type="text"
                      name="current_location"
                      className="input"
                      value={formData.current_location}
                      onChange={handleChange}
                      placeholder="Bangalore"
                    />
                  </div>

                  <div className="form-group">
                    <label className="label">Years of Experience (Optional)</label>
                    <input
                      type="number"
                      name="experience_years"
                      className={`input ${errors.experience_years ? 'error' : ''}`}
                      value={formData.experience_years}
                      onChange={handleChange}
                      min="0"
                      max="50"
                      placeholder="3"
                    />
                    {errors.experience_years && <div className="error-message">{errors.experience_years}</div>}
                  </div>
                  <div className="form-group">
                    <label className="label">Months of Experience (0-11)</label>
                    <input
                      type="number"
                      name="experience_months"
                      className={`input ${errors.experience_months ? 'error' : ''}`}
                      value={formData.experience_months}
                      onChange={handleChange}
                      min="0"
                      max="11"
                      placeholder="6"
                    />
                    {errors.experience_months && <div className="error-message">{errors.experience_months}</div>}
                  </div>
                </div>

                <div className="form-group">
                  <label className="label">Preferred Locations (Optional)</label>
                  <input
                    type="text"
                    name="preferred_location"
                    className="input"
                    value={formData.preferred_location}
                    onChange={handleChange}
                    placeholder="Bangalore, Mumbai, Remote"
                  />
                </div>

                <div className="form-group">
                  <label className="label">Profile Summary (Optional)</label>
                  <textarea
                    name="profile_summary"
                    className="input textarea"
                    value={formData.profile_summary}
                    onChange={handleChange}
                    placeholder="Brief description of your background and career goals..."
                    rows={3}
                  />
                </div>

                <div className="form-group">
                  <label className="label">Resume (Optional)</label>
                  <input
                    type="file"
                    name="resume"
                    className={`input ${errors.resume ? 'error' : ''}`}
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx"
                  />
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    Attach now and we'll save it to your profile (PDF, DOC, DOCX, max 16MB). You can also upload from your profile later.
                  </div>
                  {resumeFile && (
                    <div style={{ fontSize: '12px', color: '#28a745', marginTop: '4px' }}>
                      ✓ {resumeFile.name} selected
                    </div>
                  )}
                  {errors.resume && <div className="error-message">{errors.resume}</div>}
                </div>
              </>
            )}

            <div className="form-group">
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => {
                    setAcceptedTerms(e.target.checked);
                    if (errors.terms) {
                      setErrors((prev) => ({ ...prev, terms: '' }));
                    }
                  }}
                />
                <span>
                  I agree to the <a href={formData.role === 'partner' ? '/terms-partner.html' : '/terms.html'} onClick={(e) => openDocumentModal(e, formData.role === 'partner' ? '/terms-partner.html' : '/terms.html', 'Terms of Use')}>Terms of Use</a> and <a href="/privacy.html" onClick={(e) => openDocumentModal(e, '/privacy.html', 'Privacy Policy')}>Privacy Policy</a>.
                </span>
              </label>
              {errors.terms && <div className="error-message">{errors.terms}</div>}
            </div>

            <button
              type="submit"
              className="btn"
              disabled={loading || !isFormValid}
              style={{ width: '100%', margin: '16px 0' }}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          <div style={{ textAlign: 'center', margin: '20px 0' }}>
            <span className="muted">Already have an account? </span>
            <Link to="/login">Sign in here</Link>
          </div>
        </div>
      </div>

      {modalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={modalTitle}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(10, 15, 28, 0.72)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            zIndex: 2000
          }}
          onClick={() => setModalOpen(false)}
        >
          <div
            className="card"
            style={{
              width: 'min(900px, 92vw)',
              height: 'min(90vh, 760px)',
              padding: 0,
              overflow: 'hidden',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 20px',
                borderBottom: '1px solid #1f2937',
                background: 'var(--card)'
              }}
            >
              <div className="h3" style={{ margin: 0 }}>{modalTitle}</div>
              <button type="button" className="btn ghost" onClick={() => setModalOpen(false)}>
                Close
              </button>
            </div>
            <iframe
              title={modalTitle}
              src={modalUrl}
              style={{ border: 'none', width: '100%', flex: 1 }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;
